import {
  BadgeSpec,
  ChartKey,
  CompileWarning,
  ConstraintBundle,
  Disjunction,
  Equality,
  GapConstraint,
  OrderEdge,
  Provenance,
  ResolvedOperand,
  VarId,
  constId,
  slopeId,
  varId,
} from '../types'
import { BarPlan } from './barMapping'
import { OperandSide, resolveOperand } from './operands'
import { isCompositeName } from '../../../../common/utils/composite'

export interface CompiledCondition {
  conditionId: string
  chartKey: ChartKey
  edges: OrderEdge[]
  equalities: Equality[]
  gaps: GapConstraint[]
  disjunctions: Disjunction[]
  warnings: CompileWarning[]
  badges: BadgeSpec[]
}

export interface BuilderConditionLike {
  id: string
  chartDetails: { symbol: string; periodicity: number; timeframe: string }
  condition?: {
    LHS?: OperandSide
    conditionType?: string
    RHS?: OperandSide
    threshold?: unknown
  }
  settings?: {
    previousCandle?: {
      enabled?: boolean
      numberOfCandles?: number
      candlesNeeded?: number
    }
  }
}

const UNARY_TYPES = new Set(['moving up', 'moving down'])
const CHANGED_REL: Record<string, '=' | '>' | '<'> = {
  'changed exactly': '=',
  'changed more than': '>',
  'changed less than': '<',
}
const MAX_DISJUNCTION_OPTIONS = 64

interface Sink {
  edges: OrderEdge[]
  equalities: Equality[]
  gaps: GapConstraint[]
  neverFires: boolean
}

const newSink = (): Sink => ({ edges: [], equalities: [], gaps: [], neverFires: false })

const combinations = <T>(items: T[], size: number): T[][] => {
  if (size <= 0) return [[]]
  if (size > items.length) return []
  const [head, ...rest] = items
  const withHead = combinations(rest, size - 1).map((combo) => [head, ...combo])
  return withHead.concat(combinations(rest, size))
}

/**
 * C(n, k), capped: returns cap + 1 as soon as the count exceeds cap. MUST be
 * consulted BEFORE calling combinations() — "14 of 28 bars" is ~40M arrays,
 * and materializing them to count them is the freeze this guards against.
 */
const countCombinations = (n: number, k: number, cap: number): number => {
  if (k < 0 || k > n) return 0
  let count = 1
  for (let i = 0; i < Math.min(k, n - k); i += 1) {
    count = (count * (n - i)) / (i + 1)
    if (count > cap) return cap + 1
  }
  return Math.round(count)
}

export const getChartKey = (chartDetails: {
  symbol: string
  periodicity: number
  timeframe: string
}): ChartKey => `${chartDetails.symbol}_${chartDetails.periodicity}_${chartDetails.timeframe}`

/**
 * Compiles one condition into constraints per the PDS evaluator semantics
 * (patternUtils.ts checkConditionOnCandle + isConditionMet bar targeting).
 * Bars are per-side: each operand resolves at anchor − its own offset, and its
 * "previous" is its own bar − 1. anchorBar is `plan.now` for ordinary
 * conditions; validity-windowed conditions compile once per candidate anchor.
 */
export const compileCondition = (
  builderCondition: BuilderConditionLike,
  plan: BarPlan,
  anchorBar: number
): CompiledCondition => {
  const chartKey = getChartKey(builderCondition.chartDetails)
  const provenance: Provenance = { conditionId: builderCondition.id }
  const out: CompiledCondition = {
    conditionId: builderCondition.id,
    chartKey,
    edges: [],
    equalities: [],
    gaps: [],
    disjunctions: [],
    warnings: [],
    badges: [],
  }

  const inner = builderCondition.condition
  const type = inner?.conditionType
  if (!inner || !type) return out

  if (type === 'price movement') {
    out.warnings.push({
      kind: 'neverFires',
      conditionId: builderCondition.id,
      message: "'Price movement' can never fire — it is not implemented in the alert engine.",
    })
    return out
  }

  const lhs = resolveOperand(inner.LHS)
  const rhs = UNARY_TYPES.has(type) ? undefined : resolveOperand(inner.RHS)
  if (!lhs) return out

  // Custom (composite) indicators are opaque to the solver: their math is not
  // inverted, so the sketched series is generic rather than value-accurate.
  if (isCompositeName(lhs.indicatorName) || isCompositeName(rhs?.indicatorName)) {
    out.warnings.push({
      kind: 'notDepicted',
      conditionId: builderCondition.id,
      message:
        "Custom indicator values are illustrative — preview doesn't compute custom indicator math.",
    })
  }

  const anchorOffset = plan.now - anchorBar

  const varAt = (operand: ResolvedOperand, effectiveOffset: number, back: number): VarId => {
    if (operand.kind === 'constant') return constId(operand.value ?? NaN)
    const bar = plan.resolve(anchorOffset + effectiveOffset + back)
    return operand.kind === 'angle'
      ? slopeId(chartKey, operand.seriesId as string, bar)
      : varId(chartKey, operand.seriesId as string, bar)
  }

  const identicalAt = (lo: number, ro: number): boolean =>
    !!rhs &&
    lhs.kind !== 'constant' &&
    rhs.kind !== 'constant' &&
    lhs.kind === rhs.kind &&
    lhs.seriesId === rhs.seriesId &&
    lo === ro

  const emitCore = (lo: number, ro: number, sink: Sink, coreType: string = type): void => {
    const identical = identicalAt(lo, ro)
    const lNow = () => varAt(lhs, lo, 0)
    const lPrev = () => varAt(lhs, lo, 1)
    const rNow = () => varAt(rhs as ResolvedOperand, ro, 0)
    const rPrev = () => varAt(rhs as ResolvedOperand, ro, 1)

    // An angle-vs-constant comparison determines the PARENT line's slope sign
    // when the constant's sign makes it decidable (angle > c with c ≥ 0 forces
    // a rising line). Emit that parent edge INTO THE SOLVED GRAPH so "angle >
    // 0" + "moving down" on the same line is a detectable contradiction.
    const impliedSlopeEdges = (angleIsGreater: boolean, strict: boolean): void => {
      const pairs: Array<[ResolvedOperand | undefined, ResolvedOperand | undefined, boolean]> = [
        [lhs, rhs, angleIsGreater],
        [rhs, lhs, !angleIsGreater],
      ]
      for (const [angleSide, otherSide, greater] of pairs) {
        if (angleSide?.kind !== 'angle' || otherSide?.kind !== 'constant') continue
        const constant = otherSide.value ?? NaN
        if (Number.isNaN(constant)) continue
        const off = angleSide === lhs ? lo : ro
        const bar = plan.resolve(anchorOffset + off)
        const now = varId(chartKey, angleSide.seriesId as string, bar)
        const prev = varId(chartKey, angleSide.seriesId as string, bar - 1)
        if (greater && constant >= 0) {
          sink.edges.push({ gt: now, lt: prev, strict: strict || constant > 0, provenance })
        } else if (!greater && constant <= 0) {
          sink.edges.push({ gt: prev, lt: now, strict: strict || constant < 0, provenance })
        }
      }
    }

    switch (coreType) {
      case 'greater than':
      case 'less than': {
        if (identical) {
          sink.neverFires = true
          return
        }
        if (!rhs) return
        const [gt, lt] = coreType === 'greater than' ? [lNow(), rNow()] : [rNow(), lNow()]
        sink.edges.push({ gt, lt, strict: true, provenance })
        impliedSlopeEdges(coreType === 'greater than', true)
        return
      }
      case 'greater than or equals':
      case 'less than or equals': {
        if (identical || !rhs) return // identical is vacuously true
        const [gt, lt] = coreType === 'greater than or equals' ? [lNow(), rNow()] : [rNow(), lNow()]
        sink.edges.push({ gt, lt, strict: false, provenance })
        impliedSlopeEdges(coreType === 'greater than or equals', false)
        return
      }
      case 'equals': {
        if (identical || !rhs) return
        sink.equalities.push({ a: lNow(), b: rNow(), provenance })
        return
      }
      case 'moving up':
      case 'moving down': {
        const [gt, lt] = coreType === 'moving up' ? [lNow(), lPrev()] : [lPrev(), lNow()]
        sink.edges.push({ gt, lt, strict: true, provenance })
        return
      }
      case 'crossing up':
      case 'crossing down': {
        if (identical || !rhs) {
          sink.neverFires = true
          return
        }
        if (coreType === 'crossing up') {
          sink.edges.push({ gt: lNow(), lt: rNow(), strict: true, provenance })
          sink.edges.push({ gt: rPrev(), lt: lPrev(), strict: true, provenance })
        } else {
          sink.edges.push({ gt: rNow(), lt: lNow(), strict: true, provenance })
          sink.edges.push({ gt: lPrev(), lt: rPrev(), strict: true, provenance })
        }
        return
      }
      case 'converging':
      case 'diverging': {
        if (identical || !rhs) {
          sink.neverFires = true // gap is identically 0; PDS rejects it
          return
        }
        sink.gaps.push({
          kind: coreType as 'converging' | 'diverging',
          aMinuend: lNow(),
          aSubtrahend: rNow(),
          bMinuend: lPrev(),
          bSubtrahend: rPrev(),
          ...(coreType === 'converging' ? { sameSignPairs: true, nonZeroA: true } : {}),
          provenance,
        })
        return
      }
      case 'changed exactly':
      case 'changed more than':
      case 'changed less than': {
        const rel = CHANGED_REL[coreType]
        if (inner.threshold !== undefined) {
          const threshold = Number(inner.threshold)
          if (identical) {
            // |L − L| = 0 compared against the threshold, decidable now
            const satisfied = (rel === '=' && threshold === 0) || (rel === '<' && threshold > 0)
            if (!satisfied) sink.neverFires = true
            return
          }
          if (!rhs) return
          sink.gaps.push({
            kind: 'changedCmp',
            aMinuend: lNow(),
            aSubtrahend: rNow(),
            constant: threshold,
            rel,
            provenance,
          })
          return
        }
        // Legacy shape: RHS is the change amount, L compared to its own previous bar.
        if (rhs?.kind === 'constant') {
          sink.gaps.push({
            kind: 'changedCmp',
            aMinuend: lNow(),
            aSubtrahend: lPrev(),
            constant: rhs.value ?? NaN,
            rel,
            provenance,
          })
        }
        return
      }
      default:
        return
    }
  }

  const finish = (sink: Sink): void => {
    if (sink.neverFires) {
      out.warnings.push({
        kind: 'neverFires',
        conditionId: builderCondition.id,
        message: 'This condition can never be true — both sides are the same line.',
      })
      return
    }
    out.edges.push(...sink.edges)
    out.equalities.push(...sink.equalities)
    out.gaps.push(...sink.gaps)
  }

  const emitWindowedDisjunction = (
    windows: number[][],
    emitAt: (lo: number, ro: number, sink: Sink) => void,
    label: Disjunction['label']
  ): void => {
    const options: ConstraintBundle[] = []
    for (const window of windows) {
      const sink = newSink()
      window.forEach((offset) => emitAt(offset, offset, sink))
      if (!sink.neverFires) {
        options.push({ edges: sink.edges, equalities: sink.equalities, gaps: sink.gaps })
      }
    }
    if (options.length === 0) {
      out.warnings.push({
        kind: 'neverFires',
        conditionId: builderCondition.id,
        message: 'This condition can never be true on any bar in its window.',
      })
      return
    }
    out.disjunctions.push({ options, provenance, label })
  }

  try {
    const iteratingSide = lhs.lookback ? 'L' : rhs?.lookback ? 'R' : undefined
    // PDS gates the legacy path on `.enabled` (patternUtils.ts:573) — the UI
    // leaves {enabled:false, numberOfCandles:N} behind on deselect, so object
    // presence alone would compile bars the runtime never tests.
    const previousCandleActive =
      builderCondition.settings?.previousCandle?.enabled === true &&
      !inner.LHS?.candle &&
      !inner.RHS?.candle

    // Bare 'crossing' inside a window would need per-bar direction choices
    // (2^count combinations); depict honestly instead of guessing a direction.
    if (type === 'crossing' && (iteratingSide || previousCandleActive)) {
      out.warnings.push({
        kind: 'notDepicted',
        conditionId: builderCondition.id,
        message:
          'Preview cannot depict a direction-less crossing over a bar range — pick "crossing up" or "crossing down" to see it drawn.',
      })
      return out
    }

    if (iteratingSide) {
      const lookback = (iteratingSide === 'L' ? lhs.lookback : rhs?.lookback) as {
        count: number
        mode: 'all' | 'some'
        needed?: number
      }
      const emitAt = (windowOffset: number, sink: Sink): void => {
        const lo = iteratingSide === 'L' ? windowOffset : lhs.offset
        const ro = iteratingSide === 'R' ? windowOffset : (rhs?.offset ?? 0)
        emitCore(lo, ro, sink)
      }
      const windowOffsets = Array.from({ length: lookback.count }, (_, i) => i + 1)
      out.badges.push({
        conditionId: builderCondition.id,
        chartKey,
        kind: 'span',
        window: { from: anchorBar - lookback.count, to: anchorBar - 1 },
      })
      if (lookback.mode === 'all') {
        const sink = newSink()
        windowOffsets.forEach((offset) => emitAt(offset, sink))
        finish(sink)
      } else {
        let needed = Math.max(1, lookback.needed ?? 1)
        // Bound BEFORE generating — C(count, needed) explodes long before the
        // post-hoc length check could run ("14 of 28" is ~40M arrays).
        if (
          countCombinations(windowOffsets.length, needed, MAX_DISJUNCTION_OPTIONS) >
          MAX_DISJUNCTION_OPTIONS
        ) {
          out.warnings.push({
            kind: 'notDepicted',
            conditionId: builderCondition.id,
            message: `Preview simplifies "${needed} of ${lookback.count} bars" to a single matching bar.`,
          })
          needed = 1
        }
        const windows = combinations(windowOffsets, needed)
        const options: ConstraintBundle[] = []
        for (const window of windows) {
          const sink = newSink()
          window.forEach((offset) => emitAt(offset, sink))
          if (!sink.neverFires) {
            options.push({ edges: sink.edges, equalities: sink.equalities, gaps: sink.gaps })
          }
        }
        if (options.length === 0) {
          out.warnings.push({
            kind: 'neverFires',
            conditionId: builderCondition.id,
            message: 'This condition can never be true on any bar in its window.',
          })
        } else {
          out.disjunctions.push({ options, provenance, label: 'lookback' })
        }
      }
    } else if (previousCandleActive) {
      const { numberOfCandles, candlesNeeded } = builderCondition.settings?.previousCandle ?? {}
      // PDS defaults: undefined/null numberOfCandles → 1 (direct previous
      // candle); a stored 0 stays 0 and targets the current bar.
      const windowSize = numberOfCandles ?? 1
      const needed = candlesNeeded ?? 0
      if (needed === 0) {
        const sink = newSink()
        emitCore(windowSize, windowSize, sink)
        finish(sink)
      } else {
        const windowOffsets = Array.from({ length: windowSize }, (_, i) => i + 1)
        // Same pre-generation bound as the lookback branch above.
        let take = Math.min(needed, windowSize)
        const comboCount = countCombinations(windowSize, take, MAX_DISJUNCTION_OPTIONS)
        if (comboCount > MAX_DISJUNCTION_OPTIONS || comboCount === 0) {
          take = 1
        }
        const windows = combinations(windowOffsets, take)
        emitWindowedDisjunction(windows, (lo, ro, sink) => emitCore(lo, ro, sink), 'previousCandle')
      }
    } else if (type === 'crossing') {
      const upSink = newSink()
      const downSink = newSink()
      emitCore(lhs.offset, rhs?.offset ?? 0, upSink, 'crossing up')
      emitCore(lhs.offset, rhs?.offset ?? 0, downSink, 'crossing down')
      const options: ConstraintBundle[] = [upSink, downSink]
        .filter((sink) => !sink.neverFires)
        .map((sink) => ({ edges: sink.edges, equalities: sink.equalities, gaps: sink.gaps }))
      if (options.length === 0) {
        out.warnings.push({
          kind: 'neverFires',
          conditionId: builderCondition.id,
          message: 'This condition can never be true — both sides are the same line.',
        })
      } else {
        out.disjunctions.push({ options, provenance, label: 'crossing' })
      }
    } else {
      const sink = newSink()
      emitCore(lhs.offset, rhs?.offset ?? 0, sink)
      finish(sink)
    }
  } catch {
    out.warnings.push({
      kind: 'notDepicted',
      conditionId: builderCondition.id,
      message: 'This condition targets bars beyond the preview window.',
    })
    return out
  }

  // Badges on the anchor geometry (best-effort; renderer tolerates missing bars)
  const badgeBar = (offset: number): number | undefined => {
    const bar = anchorBar - offset
    return bar >= 0 ? bar : undefined
  }
  if (type === 'moving up' || type === 'moving down') {
    out.badges.push({
      conditionId: builderCondition.id,
      chartKey,
      kind: type === 'moving up' ? 'up' : 'down',
      seriesId: lhs.seriesId,
      bar: badgeBar(lhs.offset),
    })
  }
  if (type.startsWith('crossing')) {
    out.badges.push({
      conditionId: builderCondition.id,
      chartKey,
      kind: 'cross',
      seriesId: lhs.seriesId,
      bar: badgeBar(lhs.offset),
    })
  }
  if (CHANGED_REL[type]) {
    out.badges.push({
      conditionId: builderCondition.id,
      chartKey,
      kind: 'gapBracket',
      seriesId: lhs.seriesId,
      bar: badgeBar(lhs.offset),
    })
  }
  for (const operand of [lhs, rhs]) {
    if (operand && operand.kind !== 'constant' && operand.offset > 0) {
      out.badges.push({
        conditionId: builderCondition.id,
        chartKey,
        kind: 'guide',
        seriesId: operand.seriesId,
        bar: badgeBar(operand.offset),
      })
    }
  }

  return out
}
